import { useEffect, useState, useCallback } from "react";
import { Loader2, Download } from "lucide-react";
import { api } from "@/lib/api";
import { ASSETS } from "@/config/assets";
import { QuizCard } from "@/components/QuizCard";
import { CarrotGame } from "@/components/CarrotGame";
import { SocialLinks } from "@/components/SocialLinks";

export default function Home() {
  const [preds, setPreds] = useState(null);
  const [bookmakers, setBookmakers] = useState([]);
  const [installEvent, setInstallEvent] = useState(null);

  const load = useCallback(async () => {
    const [{ data: p }, { data: bms }] = await Promise.all([
      api.get("/predictions/active"),
      api.get("/bookmakers"),
    ]);
    setPreds(p);
    setBookmakers(bms);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallEvent(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    await installEvent.userChoice;
    setInstallEvent(null);
  };

  return (
    <div className="space-y-8">
      {/* Hero */}
      <section className="overflow-hidden rounded-3xl border-2 border-zinc-950 bg-zinc-950 shadow-[6px_6px_0_0_#22c55e]">
        <div data-testid="hero-mascot" className="relative flex justify-center bg-white">
          <img
            src={ASSETS.tony3d}
            alt="Il Mago Tony"
            className="max-h-[380px] w-auto object-contain"
            draggable={false}
          />
        </div>
        <div className="space-y-4 p-5">
          <p className="font-display text-2xl font-black uppercase leading-tight text-white">
            Fai il tuo pronostico con <span className="text-green-400">Il Mago Tony</span>!
          </p>
          {/* Gioco giornaliero "Trova la carota" */}
          <CarrotGame />
          {installEvent && (
            <button data-testid="pwa-install-button" onClick={install}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-green-500 py-3 text-sm font-black uppercase tracking-wide text-zinc-950 transition-colors hover:bg-green-400">
              <Download className="h-4 w-4" /> Installa Il Mago Tony
            </button>
          )}
        </div>
      </section>

      {/* Explanation */}
      <section data-testid="home-explanation" className="rounded-3xl border border-slate-200 bg-white p-6">
        <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-zinc-950">
          Come funziona <span className="text-green-500">Il Mago Tony</span>
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-zinc-600">
          Il Mago Tony è la zona dove condividi <strong>gratis</strong> i tuoi pronostici, rispondendo a quiz a risposta
          multipla su marcatore, esito fisso e altre opzioni legate al match del momento. Le risposte corrette e i
          pronostici azzeccati ti fanno accumulare <strong>punti</strong> per scalare la classifica e vincere i
          <strong> premi messi in palio da Tony</strong>. Una sola risposta per quiz: scegli con testa, poi si congela!
        </p>
        <div className="mt-4">
          <h3 className="font-display text-lg font-black uppercase tracking-tight text-zinc-950">Sezione pubblica</h3>
          <p className="mt-1 text-sm leading-relaxed text-zinc-600">
            Nella sezione pubblica puoi rispondere gratis ai pronostici di Tony. Chi accumula più punti nella classifica
            vince il premio scelto da Tony.
          </p>
        </div>
        <div className="mt-4">
          <h3 className="font-display text-lg font-black uppercase tracking-tight text-zinc-950">Sezione <span className="text-green-500">VIP</span></h3>
          <p className="mt-1 text-sm leading-relaxed text-zinc-600">
            Nella sezione VIP il gioco non cambia: rispondi sempre ai pronostici. Cambiano i premi! Tony mette in palio
            premi esclusivi e non solo: esperienze fantastiche in tutti gli stadi d'Italia, con biglietti VIP e tanto
            altro. Chi entra nel VIP ha accesso anche al gruppo esclusivo della <strong>BAND E FESS</strong>, dove riceve
            notizie e pronostici esclusivi, e alla chat insieme a Tony.
          </p>
        </div>
      </section>

      {/* Social block */}
      <section data-testid="home-social-top" className="rounded-3xl border-2 border-zinc-950 bg-green-500/10 p-6">
        <SocialLinks />
      </section>

      {/* Active predictions */}
      <section>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-zinc-950">Pronostici di oggi</h2>
          <span className="rounded-full bg-zinc-950 px-3 py-1 text-xs font-bold text-green-400">{preds ? preds.length : 0} attivi</span>
        </div>
        {preds === null ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-green-500" /></div>
        ) : preds.length === 0 ? (
          <div data-testid="no-predictions" className="rounded-3xl border-2 border-dashed border-slate-300 bg-white p-10 text-center">
            <p className="font-display text-xl font-bold uppercase text-zinc-400">Nessun pronostico attivo</p>
            <p className="mt-1 text-sm text-zinc-500">Torna presto: Tony sta preparando le prossime sfide!</p>
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-2">
            {preds.map((p) => <QuizCard key={p.id} pred={p} onAnswered={load} bookmakers={bookmakers} />)}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer data-testid="home-footer" className="rounded-3xl bg-zinc-950 p-6 text-center">
        <p className="font-display text-xl font-black uppercase text-white">
          IL MAGO <span className="text-green-400">TONY</span>
        </p>
        <p className="mt-2 text-sm text-zinc-400">Contatti · Seguimi sui canali ufficiali per premi e novità.</p>
      </footer>
    </div>
  );
}
